import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, TriangleAlert } from "lucide-react";
import {
  Alert,
  AlertDescription,
  AvatarFallback,
  Avatar,
  Badge,
  Button,
  ConfirmModal,
  PageHeader,
  SearchField,
  SegmentedControl,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import { AffiliateNetworkStatus, type AffiliateNetwork, type NetworkNumbers } from "@gen/tracker/v1/affiliate_network_pb";

import { ListView } from "@/components/list-view";
import { WriteOnly } from "@/components/auth/write-only";
import { PARTNER_TABS, PartnerCard, PostbackLine, type PartnerTab } from "@/components/partners/partner-card";
import { PartnerEditor } from "@/components/partners/partner-editor";
import { modelCode } from "@/components/offers/labels";
import { useT, type TKey } from "@/lib/i18n";
import { DASH, formatInt, formatMoney } from "@/lib/format";
import { useCrumbTail } from "@/lib/crumb";
import { carryPeriod } from "@/lib/route-tab";
import { useCanWrite } from "@/lib/permissions";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { useDebounced } from "@/lib/hooks/use-debounced";
import { listSort } from "@/lib/services/catalog";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useArchiveAffiliateNetwork, useGetAffiliateNetwork, useListAffiliateNetworks, useNetworkNumbers } from "@/lib/services/network-service";

/**
 * Партнёрки (board PartnerList; the card and the editor are drawers over it
 * — PartnerCard, PartnerEditor). /library/networks[/:id[/:tab]], `new` and
 * `edit` open the editor. The numbers are the Top bar's period
 * (ListNetworkNumbers); «Постбек» is the newest journaled postback and the
 * partner's own silence alert; «Статусы» how many of its words are read,
 * the unmapped ones flagged — and named in the banner over the table.
 */

const PB_FILTERS = ["all", "arrives", "silent", "none"] as const;
type PbFilter = (typeof PB_FILTERS)[number];
const PB_LABEL: Record<PbFilter, TKey> = { all: "u6.pbf.all", arrives: "u6.pbf.arrives", silent: "u6.pbf.silent", none: "u6.pbf.none" };

export default function PartnersPage() {
  const t = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id: routeId, tab: routeTab } = useParams();
  const canWrite = useCanWrite();
  const params = useTableParams({ defaultSort: [{ id: "name", desc: false }] });
  const pb = (PB_FILTERS as readonly string[]).includes(params.getFilter("pb")) ? (params.getFilter("pb") as PbFilter) : "all";
  const [archiving, setArchiving] = useState<AffiliateNetwork | null>(null);

  const q = useDebounced(params.search.trim());
  const list = useListAffiliateNetworks({
    search: q,
    sort: listSort(params.sorting, { name: "name" }),
    limit: params.pageSize,
    offset: params.pageIndex * params.pageSize,
  });
  const rows = useMemo(() => (list.data?.items ?? []).filter((n) => n.status !== AffiliateNetworkStatus.ARCHIVED), [list.data]);
  const numbers = useNetworkNumbers({ ids: rows.map((n) => n.id), range: { from: params.from, to: params.to }, timezone: params.tz, basis: params.basis });
  const byId = useMemo(() => new Map((numbers.data?.items ?? []).map((n) => [n.networkId, n])), [numbers.data]);
  const shown = useMemo(
    () =>
      rows.filter((n) => {
        const x = byId.get(n.id);
        if (pb === "all" || !x) return true;
        if (pb === "silent") return x.silent;
        if (pb === "none") return !x.lastPostbackAt;
        return !!x.lastPostbackAt && !x.silent;
      }),
    [rows, byId, pb],
  );
  const attention = rows.map((n) => ({ n, x: byId.get(n.id) })).filter((r) => r.x && r.x.statusWordsUnmapped > 0);

  const creating = routeId === "new";
  const openId = routeId && !creating ? routeId : undefined;
  const editing = !!openId && routeTab === "edit";
  const tab: PartnerTab = (PARTNER_TABS as readonly string[]).includes(routeTab ?? "") ? (routeTab as PartnerTab) : "postback";
  const opened = useGetAffiliateNetwork(openId);
  const openedNumbers = useNetworkNumbers({ ids: openId ? [openId] : [], range: { from: params.from, to: params.to }, timezone: params.tz, basis: params.basis });
  useCrumbTail(creating ? t("u6.pe.titleNew") : opened.data?.name);
  const base = "/library/networks";
  const go = (path: string) => navigate(`${path}${carryPeriod(search)}`);
  const archive = useArchiveAffiliateNetwork();

  const columns = useMemo<ColumnDef<AffiliateNetwork>[]>(
    () => [
      {
        id: "name",
        header: t("u6.col.partner"),
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Avatar className="size-6 rounded-md">
              <AvatarFallback className="rounded-md text-xs">{row.original.name.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="truncate font-medium">{row.original.name}</span>
          </span>
        ),
      },
      { id: "model", header: t("u6.col.model"), enableSorting: false, size: 90, cell: ({ row }) => modelCode(row.original.defaultPayoutModel) },
      { id: "currency", header: t("u6.col.currency"), enableSorting: false, size: 80, cell: ({ row }) => row.original.currency },
      {
        id: "postback",
        header: t("u6.col.postback"),
        enableSorting: false,
        size: 230,
        cell: ({ row }) => <PostbackLine numbers={byId.get(row.original.id)} />,
      },
      {
        id: "statuses",
        header: t("u6.col.statuses"),
        enableSorting: false,
        size: 190,
        cell: ({ row }) => <StatusesCell x={byId.get(row.original.id)} />,
      },
      { id: "offers", header: t("u6.col.offers"), enableSorting: false, size: 90, meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{formatInt(byId.get(row.original.id)?.offerCount || undefined)}</span> },
      { id: "leads", header: t("u6.col.leads"), enableSorting: false, size: 80, meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{formatInt(statsRowToNumbers(byId.get(row.original.id)?.stats).leads || undefined)}</span> },
      { id: "revenue", header: t("u6.col.revenueUsd"), enableSorting: false, size: 100, meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{formatMoney(statsRowToNumbers(byId.get(row.original.id)?.stats).revenue || undefined)}</span> },
    ],
    [t, byId],
  );

  const totals = numbers.data?.totals ? statsRowToNumbers(numbers.data.totals) : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-4">
        <PageHeader
          title={t("u6.partners.title")}
          subtitle={t("u6.partners.subtitle")}
          actions={
            <WriteOnly lock={t("u6.partners.new")}>
              <Button variant="primary" onClick={() => go(`${base}/new`)}>
                <Plus className="size-4" /> {t("u6.partners.new")}
              </Button>
            </WriteOnly>
          }
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
        {attention.map(({ n, x }) => (
          <Alert key={n.id} variant="warn" className="flex items-center gap-3">
            <TriangleAlert className="size-4" aria-hidden />
            <AlertDescription className="flex-1">
              <b className="font-semibold">{n.name}:</b> {t("u6.partners.unmapped", { n: x!.statusWordsUnmapped, leads: formatInt(Number(x!.unmappedLeads)) })}
            </AlertDescription>
            <Button variant="secondary" size="sm" onClick={() => go(`${base}/${n.id}`)}>
              {t("u6.partners.map")}
            </Button>
          </Alert>
        ))}
        <ListView
          columns={columns}
          data={shown}
          getRowId={(n) => n.id}
          loading={list.isLoading}
          failure={list.error ? { title: t("u6.partners.loadFailed"), error: list.error, retry: () => void list.refetch() } : undefined}
          onRowClick={(n) => go(`${base}/${n.id}`)}
          rowClassName={(n) => (n.id === openId ? "bg-surface-2" : undefined)}
          manualSorting
          sorting={params.sorting}
          onSortingChange={params.onSortingChange}
          density="compact"
          pagination={{
            pageIndex: params.pageIndex,
            pageSize: params.pageSize,
            totalCount: list.data?.total ?? 0,
            onPageIndexChange: params.setPageIndex,
            onPageSizeChange: params.setPageSize,
            pageSizeOptions: params.pageSizeOptions,
          }}
          totals={
            totals
              ? {
                  name: t("u6.partners.totals", { n: shown.length }),
                  offers: formatInt(shown.reduce((s, n) => s + (byId.get(n.id)?.offerCount ?? 0), 0)),
                  leads: formatInt(totals.leads),
                  revenue: formatMoney(totals.revenue),
                }
              : undefined
          }
          toolbarLeft={
            <>
              <SearchField value={params.search} onChange={params.setSearch} label={t("u6.partners.search")} placeholder={t("u6.partners.search")} className="w-70" />
              <SegmentedControl
                aria-label={t("u6.col.postback")}
                value={pb}
                onValueChange={(v) => params.setFilters({ pb: v === "all" ? null : v })}
                options={PB_FILTERS.map((f) => ({ value: f, label: t(PB_LABEL[f]) }))}
              />
            </>
          }
          emptyMessage={t("u6.partners.none")}
        />
      </div>

      {openId && !editing ? (
        <PartnerCard
          network={opened.data}
          loading={opened.isLoading}
          error={opened.error}
          numbers={openedNumbers.data?.items[0]}
          tab={tab}
          onTab={(next) => go(next === "postback" ? `${base}/${openId}` : `${base}/${openId}/${next}`)}
          onClose={() => go(base)}
          onEdit={canWrite ? () => go(`${base}/${openId}/edit`) : undefined}
          onArchive={canWrite ? () => opened.data && setArchiving(opened.data) : undefined}
        />
      ) : null}
      {creating || (editing && opened.data) ? (
        <PartnerEditor
          open
          network={editing ? opened.data : undefined}
          onClose={() => go(editing ? `${base}/${openId}` : base)}
          onSaved={(n) => go(`${base}/${n.id}`)}
          onArchive={editing && opened.data ? () => setArchiving(opened.data!) : undefined}
        />
      ) : null}
      <ConfirmModal
        open={!!archiving}
        onClose={() => setArchiving(null)}
        title={t("u6.archive.partnerTitle", { name: archiving?.name ?? "" })}
        description={t("u6.archive.partnerBody")}
        confirmLabel={t("u6.common.archive")}
        cancelLabel={t("u6.common.cancel")}
        tone="danger"
        loading={archive.isPending}
        onConfirm={async () => {
          if (!archiving) return;
          try {
            await archive.mutateAsync(archiving.id);
            toast.success(t("u6.archive.done"));
            setArchiving(null);
            go(base);
          } catch (err) {
            toast.error(err instanceof Error ? err.message : t("u6.archive.failed"));
          }
        }}
      />
    </div>
  );
}

function StatusesCell({ x }: { x?: NetworkNumbers }) {
  const t = useT();
  if (!x) return <span className="text-muted-foreground">{DASH}</span>;
  const total = x.statusWordsKnown + x.statusWordsUnmapped;
  if (total === 0) return <span className="text-muted-foreground">{DASH}</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className={x.statusWordsUnmapped ? "text-warn" : undefined}>{t("u6.partners.words", { known: x.statusWordsKnown, total })}</span>
      {x.statusWordsUnmapped ? <Badge variant="warn">{t("u6.partners.newWords", { n: x.statusWordsUnmapped })}</Badge> : null}
    </span>
  );
}
